import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'carbon-react/lib/components/box';
import { Option, Select } from 'carbon-react/lib/components/select';

import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import { AccountCategory } from '../../../../forecast/prediction-builder/types';
import { ASSET_CATEGORIES_PARENT, COA_SWITCH_RULE_FORM_FIELD_IDS } from './constants';
import { Typography } from 'carbon-react/lib/components/typography/typography.component';
import { COADisplayAccount } from '../../../../chart-of-accounts/types';

interface Props {
  onClose: () => void;
  onSave: () => void;
  error?: string;
  validationErrors?: Record<string, { id: string; message: string }>;
  switchRuleCategory: string;
  setSwitchRuleCategory: (category: string) => void;
  categories: string[];
  revalidate: () => void;
  accountCategoryParent: string;
  account: COADisplayAccount;
}
export const SwitchRuleFormScreen = ({
  onClose,
  onSave,
  error,
  validationErrors,
  switchRuleCategory,
  setSwitchRuleCategory,
  categories,
  revalidate,
  accountCategoryParent,
  account
}: Props) => {
  const intl = useIntl();

  const { accountCategoryNames } = useLocalisedConstantsContext();

  const handleCategoryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setSwitchRuleCategory(event.target.value),
    [setSwitchRuleCategory]
  );

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={onSave}
      createText={<FormattedMessage id="common.save" defaultMessage="Save" />}
    >
      {error && <WizardFormError message={error} mb={3} />}

      <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

      <WizardRequiredFieldInfo />

      <Box mt={2} mb={3}>
        {accountCategoryParent === ASSET_CATEGORIES_PARENT ? (
          <Typography mb={0} fontSize="14px" color="rgba(0, 0, 0, 0.65)">
            <FormattedMessage
              id="predict.negative_switch_explain_asset_bold"
              defaultMessage="<b>{accountName}</b> is an asset. If values are negative you can use the switch rule to move to a liability."
              values={{ accountName: account?.name, b: chunks => <span style={{ fontWeight: '700' }}>{chunks}</span> }}
            />
          </Typography>
        ) : (
          <Typography mb={0} fontSize="14px" color="rgba(0, 0, 0, 0.65)">
            <FormattedMessage
              id="predict.negative_switch_explain_category_bold"
              defaultMessage="<b>{accountName}</b> is a liability. If values are negative you can use the switch rule to move to an asset."
              values={{ accountName: account?.name, b: chunks => <span style={{ fontWeight: '700' }}>{chunks}</span> }}
            />
          </Typography>
        )}
      </Box>

      <Box mb={3}>
        <Select
          required
          name={COA_SWITCH_RULE_FORM_FIELD_IDS.CATEGORY}
          id={COA_SWITCH_RULE_FORM_FIELD_IDS.CATEGORY}
          mb={0}
          value={switchRuleCategory}
          autoComplete="off"
          label={intl.formatMessage({
            id: 'predict.category_switch_to_if_values_move_negative',
            defaultMessage: 'Category to switch to if values move negative'
          })}
          onChange={handleCategoryChange}
          placeholder={intl.formatMessage({
            id: 'common.predict.please_select',
            defaultMessage: 'Please select...'
          })}
          onBlur={revalidate}
        >
          {categories.map(c => (
            <Option key={c} value={c} text={accountCategoryNames[c as AccountCategory]} />
          ))}
        </Select>
      </Box>
    </WizardScreen>
  );
};
